class RaptorTheme {
  constructor() {
    this.overlay = document.querySelector('[data-drawer-overlay]');
    this.initNavigation();
    this.initStickyHeader();
    this.initSearch();
    this.initHeroSliders();
    this.initProduct();
    this.initRecommendations();
    this.initFacets();
    this.initQuantities();
    this.initAddresses();
    this.initSiteModal();
  }

  openOverlay() {
    if (this.overlay) this.overlay.hidden = false;
    document.body.classList.add('drawer-open');
  }

  closeOverlay() {
    if (this.overlay) this.overlay.hidden = true;
    document.body.classList.remove('drawer-open');
  }

  initNavigation() {
    const menu = document.querySelector('[data-mobile-menu]');
    const open = document.querySelector('[data-menu-toggle]');
    const close = document.querySelector('[data-menu-close]');
    if (!menu || !open) return;
    const hide = () => {
      menu.classList.remove('is-open');
      open.setAttribute('aria-expanded', 'false');
      this.closeOverlay();
    };
    open.addEventListener('click', () => {
      menu.classList.add('is-open');
      open.setAttribute('aria-expanded', 'true');
      this.openOverlay();
    });
    close?.addEventListener('click', hide);
    this.overlay?.addEventListener('click', hide);
    menu.querySelectorAll('.site-nav__submenu-toggle').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const item = toggle.closest('.site-nav__item');
        const isOpen = item.classList.toggle('submenu-open');
        toggle.setAttribute('aria-expanded', String(isOpen));
      });
    });
  }

  initStickyHeader() {
    const header = document.querySelector('[data-site-header]');
    if (!header?.classList.contains('site-header--sticky')) return;
    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 34);
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  initSearch() {
    const panel = document.querySelector('[data-header-search]');
    const toggle = document.querySelector('[data-search-toggle]');
    const close = document.querySelector('[data-search-close]');
    if (!panel || !toggle) return;
    const hide = () => { panel.hidden = true; };
    toggle.addEventListener('click', () => {
      panel.hidden = !panel.hidden;
      if (!panel.hidden) panel.querySelector('input')?.focus();
    });
    close?.addEventListener('click', hide);
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') hide(); });
  }

  initHeroSliders() {
    document.querySelectorAll('[data-hero-slider]').forEach((slider) => {
      const slides = [...slider.querySelectorAll('[data-slide]')];
      const dots = [...slider.querySelectorAll('[data-slide-dot]')];
      const pause = slider.querySelector('[data-slider-pause]');
      if (slides.length < 2) return;
      let index = 0;
      let playing = slider.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let timer;
      const show = (next) => {
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      };
      const start = () => {
        window.clearInterval(timer);
        if (playing) timer = window.setInterval(() => show(index + 1), Number(slider.dataset.speed) || 5000);
      };
      dots.forEach((dot, i) => dot.addEventListener('click', () => { show(i); start(); }));
      pause?.addEventListener('click', () => {
        playing = !playing;
        pause.textContent = playing ? 'Ⅱ' : '▶';
        pause.setAttribute('aria-label', playing ? 'Mettre le diaporama en pause' : 'Lire le diaporama');
        start();
      });
      start();
    });
  }

  initProduct() {
    document.querySelectorAll('[data-product-root]').forEach((root) => {
      root.querySelectorAll('[data-media-target]').forEach((button) => {
        button.addEventListener('click', () => {
          root.querySelectorAll('[data-media-id]').forEach((media) => media.classList.toggle('product-media--hidden', media.dataset.mediaId !== button.dataset.mediaTarget));
          root.querySelectorAll('[data-media-target]').forEach((thumb) => thumb.classList.toggle('is-active', thumb === button));
        });
      });
      const select = root.querySelector('[data-variant-select]');
      const json = root.querySelector('[data-product-json]');
      const form = root.querySelector('.product-form');
      const addSubmit = root.querySelector('[data-add-to-cart]');
      const submitButtons = [...root.querySelectorAll('[data-product-submit]')];
      const stock = root.querySelector('[data-stock-status]');
      const stockText = root.querySelector('[data-stock-text]');
      const price = root.querySelector('[data-price]');
      if (!select || !json || !form || !addSubmit || !submitButtons.length) return;
      const variants = JSON.parse(json.textContent);
      const money = new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
        style: 'currency',
        currency: root.dataset.currency || 'EUR'
      });
      const setPurchaseAvailability = (available) => {
        submitButtons.forEach((button) => { button.disabled = !available; });
      };
      const applyVariant = (variant) => {
        if (!variant) {
          select.value = '';
          setPurchaseAvailability(false);
          addSubmit.textContent = root.dataset.soldOutLabel;
          stock?.classList.add('is-unavailable');
          if (stockText) stockText.textContent = root.dataset.soldOutLabel;
          return;
        }
        select.value = String(variant.id);
        setPurchaseAvailability(variant.available);
        addSubmit.textContent = variant.available ? root.dataset.addLabel : root.dataset.soldOutLabel;
        stock?.classList.toggle('is-unavailable', !variant.available);
        if (stockText) stockText.textContent = variant.available ? root.dataset.inStockLabel : root.dataset.soldOutLabel;
        if (price) {
          const current = `<span class="price__current">${money.format(variant.price / 100)}</span>`;
          const compare = variant.compare_at_price > variant.price ? `<s class="price__compare">${money.format(variant.compare_at_price / 100)}</s>` : '';
          price.innerHTML = `${compare}${current}`;
          price.classList.toggle('price--sale', Boolean(compare));
        }
        if (variant.featured_media) root.querySelector(`[data-media-target="${variant.featured_media.id}"]`)?.click();
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      };
      select.addEventListener('change', () => {
        applyVariant(variants.find((item) => String(item.id) === select.value));
      });
      root.querySelectorAll('[data-product-option]').forEach((option) => {
        option.addEventListener('change', () => {
          const selected = [...root.querySelectorAll('[data-product-option]:checked')]
            .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
            .map((input) => input.value);
          applyVariant(variants.find((variant) => variant.options.every((value, index) => value === selected[index])));
        });
      });
      form.addEventListener('submit', (event) => {
        const selectedVariant = variants.find((variant) => String(variant.id) === select.value);
        if (!selectedVariant?.available || form.dataset.submitting === 'true') {
          event.preventDefault();
          return;
        }
        form.dataset.submitting = 'true';
        form.setAttribute('aria-busy', 'true');
        submitButtons.forEach((button) => { button.disabled = true; });
      });
      window.addEventListener('pageshow', () => {
        delete form.dataset.submitting;
        form.removeAttribute('aria-busy');
        applyVariant(variants.find((variant) => String(variant.id) === select.value));
      });
    });
  }

  initQuantities() {
    document.querySelectorAll('[data-quantity]').forEach((control) => {
      const input = control.querySelector('input');
      control.querySelector('[data-quantity-minus]')?.addEventListener('click', () => { input.value = Math.max(1, Number(input.value) - 1); });
      control.querySelector('[data-quantity-plus]')?.addEventListener('click', () => { input.value = Number(input.value) + 1; });
    });
  }

  initRecommendations() {
    document.querySelectorAll('[data-recommendations-url]').forEach((section) => {
      const url = section.dataset.recommendationsUrl;
      if (!url) return;
      fetch(url)
        .then((response) => response.text())
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const content = parsed.querySelector('[data-recommendations-content]');
          if (content?.innerHTML.trim()) section.querySelector('[data-recommendations-content]').innerHTML = content.innerHTML;
          else section.hidden = true;
        })
        .catch(() => { section.hidden = true; });
    });
  }

  initFacets() {
    const facets = document.querySelector('[data-facets]');
    const open = document.querySelector('[data-filter-toggle]');
    const close = document.querySelector('[data-filter-close]');
    if (!facets || !open) return;
    const hide = () => { facets.classList.remove('is-open'); this.closeOverlay(); };
    open.addEventListener('click', () => { facets.classList.add('is-open'); this.openOverlay(); });
    close?.addEventListener('click', hide);
    this.overlay?.addEventListener('click', hide);
  }

  initAddresses() {
    document.querySelectorAll('[data-address-delete]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!window.confirm(button.dataset.confirmMessage || 'Supprimer cette adresse ?')) return;
        const form = document.createElement('form');
        form.method = 'post';
        form.action = button.dataset.target;
        form.innerHTML = '<input type="hidden" name="_method" value="delete">';
        document.body.appendChild(form);
        form.submit();
      });
    });
  }

  initSiteModal() {
    const dialog = document.querySelector('[data-site-modal]');
    const title = dialog?.querySelector('[data-site-modal-title]');
    const content = dialog?.querySelector('[data-site-modal-content]');
    if (!dialog || !title || !content) return;

    let lastTrigger = null;
    let fallbackInertElements = [];

    const templateFor = (key) => [...document.querySelectorAll('[data-site-modal-template]')]
      .find((template) => template.dataset.siteModalTemplate === key);

    const setExpanded = (key, expanded) => {
      document.querySelectorAll('[data-site-modal-open]').forEach((trigger) => {
        if (trigger.dataset.siteModalOpen === key) trigger.setAttribute('aria-expanded', String(expanded));
      });
    };

    const releaseFallbackInert = () => {
      fallbackInertElements.forEach((element) => { element.inert = false; });
      fallbackInertElements = [];
    };

    const cleanup = () => {
      const key = dialog.dataset.siteModalKey;
      if (key) setExpanded(key, false);
      document.body.classList.remove('site-modal-open');
      releaseFallbackInert();
      delete dialog.dataset.siteModalKey;
      content.replaceChildren();
      title.textContent = '';
      if (lastTrigger?.isConnected) lastTrigger.focus();
      lastTrigger = null;
    };

    const closeDialog = () => {
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
      else {
        dialog.removeAttribute('open');
        cleanup();
      }
    };

    const openDialog = (key, trigger) => {
      const template = templateFor(key);
      if (!template) return;

      if (dialog.open) closeDialog();
      const mobileMenu = document.querySelector('[data-mobile-menu]');
      const menuToggle = document.querySelector('[data-menu-toggle]');
      const openedFromMobileMenu = Boolean(mobileMenu?.contains(trigger));
      mobileMenu?.classList.remove('is-open');
      menuToggle?.setAttribute('aria-expanded', 'false');
      this.closeOverlay();
      const searchPanel = document.querySelector('[data-header-search]');
      if (searchPanel) searchPanel.hidden = true;

      lastTrigger = openedFromMobileMenu ? menuToggle : trigger;
      dialog.dataset.siteModalKey = key;
      title.textContent = template.dataset.title || '';
      content.replaceChildren(template.content.cloneNode(true));
      setExpanded(key, true);
      document.body.classList.add('site-modal-open');

      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
        fallbackInertElements = [...document.body.children].filter((element) => element !== dialog);
        fallbackInertElements.forEach((element) => { element.inert = true; });
      }
      window.requestAnimationFrame(() => dialog.querySelector('[data-site-modal-close]')?.focus());
    };

    document.addEventListener('click', (event) => {
      const openButton = event.target.closest('[data-site-modal-open]');
      if (openButton) {
        event.preventDefault();
        openDialog(openButton.dataset.siteModalOpen, openButton);
        return;
      }
      if (event.target.closest('[data-site-modal-close]')) closeDialog();
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog();
    });
    dialog.addEventListener('close', cleanup);
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDialog();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...dialog.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true');
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const returnedForm = window.location.hash === '#SiteWithdrawalForm'
      ? { key: 'withdrawal', template: templateFor('withdrawal') }
      : window.location.hash === '#SiteContactForm'
        ? { key: 'contact', template: templateFor('contact') }
        : null;
    if (returnedForm?.template?.content.querySelector('.site-dialog__success, .site-dialog__errors')) {
      openDialog(returnedForm.key, null);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => new RaptorTheme());
